import { SIMARouteType } from "@earthbrain/sima";
import { BoundingSphere, Math as CMath, Cartesian2, Cartesian3, Color, HeadingPitchRange, LabelCollection, LabelStyle, Material, PolylineCollection, PrimitiveCollection, VerticalOrigin, } from "cesium";
import { Site } from "../site/site";
import { EarthBrainUtils } from "../utilities/earthbrainutility";
export class CesiumRenderableSIMA {
    _sima;
    _options;
    _primitives;
    _polylines;
    _labels;
    _viewer;
    _renderData;
    constructor(sima, options, viewer) {
        this._sima = sima;
        this._options = options;
        this._viewer = viewer;
        this._renderData = {
            surveyPoints_wgs84: new Map(),
            surveyPointSpheres: new Map(),
            routes: [],
        };
    }
    get id() {
        return this._sima.id;
    }
    /**
     * Gets the injected SIMA document instance.
     */
    get simaDoc() {
        return this._sima.simaDoc;
    }
    get sima() {
        return this._sima;
    }
    get pointsToCreateCrossSection() {
        return this._sima.pointsToCreateCrossSection;
    }
    getDirectionAtCenterlinePoint(pointId) {
        return this._sima.getDirectionAtCenterlinePoint(pointId);
    }
    getCesiumSphereEntity(id) {
        return this._renderData.surveyPointSpheres.get(id);
    }
    // Initialize
    static async create(sima, options, viewer) {
        const renderable = new CesiumRenderableSIMA(sima, options, viewer);
        const original_positions = sima.surveyPoints.map((sp) => sp.position);
        let positions = original_positions;
        if (options?.swapXY) {
            positions = positions.map((pos) => {
                return new Cartesian3(pos.y, pos.x, pos.z);
            });
        }
        const surveyPoints_wgs84_positions = await Site.getInstance().convertPointsToWGS84(positions);
        for (let i = 0; i < sima.surveyPoints.length; i++) {
            renderable._renderData.surveyPoints_wgs84.set(renderable._getKeyOfSurveyPoint(sima.surveyPoints[i]), {
                point_number: sima.surveyPoints[i].point_number,
                point_name: sima.surveyPoints[i].point_name,
                position: surveyPoints_wgs84_positions[i],
            });
        }
        for (let i = 0; i < sima.routeByGroup.length; i++) {
            const routeData = {
                ipLine_wgs84: { points: [], rawRoute: undefined },
                mainLine_wgs84: {
                    points: [],
                    rawRoute: undefined,
                },
                centerLine_wgs84: { points: [], rawRoute: undefined },
            };
            const ipLineRoute = sima.routeByGroup[i].ipLine;
            if (ipLineRoute) {
                const ipPoints = ipLineRoute.points
                    .filter((pt) => Boolean(pt))
                    .map((pt) => {
                    return renderable._renderData.surveyPoints_wgs84.get(renderable._getKeyOfSurveyPoint(pt));
                })
                    .filter((pt) => Boolean(pt));
                routeData.ipLine_wgs84 = { points: ipPoints, rawRoute: ipLineRoute };
            }
            const mainLineRoute = sima.routeByGroup[i].mainLine;
            if (mainLineRoute) {
                const original_mainLinePoints = mainLineRoute.points.map((pt) => pt.position);
                let mainLinePoints = original_mainLinePoints;
                if (options?.swapXY) {
                    mainLinePoints = mainLinePoints.map((pt) => {
                        return new Cartesian3(pt.y, pt.x, pt.z);
                    });
                }
                const mainLinePoints_wgs84Positions = await Site.getInstance().convertPointsToWGS84(mainLinePoints);
                const mainLinePoints_wgs84 = mainLinePoints_wgs84Positions.map((pt) => {
                    return {
                        point_number: -1,
                        point_name: "",
                        position: pt,
                    };
                });
                routeData.mainLine_wgs84 = {
                    points: mainLinePoints_wgs84,
                    rawRoute: mainLineRoute,
                };
            }
            const centerLineRoute = sima.routeByGroup[i].centerLine;
            if (centerLineRoute) {
                const original_centerLinePoints = centerLineRoute.points.map((pt) => pt.position);
                let centerLinePoints = original_centerLinePoints;
                if (options?.swapXY) {
                    centerLinePoints = centerLinePoints.map((pt) => {
                        return new Cartesian3(pt.y, pt.x, pt.z);
                    });
                }
                const centerLinePoints_wgs84Positions = await Site.getInstance().convertPointsToWGS84(centerLinePoints);
                const centerLinePoints_wgs84 = centerLinePoints_wgs84Positions.map((pt) => {
                    return {
                        point_number: -1,
                        point_name: "",
                        position: pt,
                    };
                });
                routeData.centerLine_wgs84 = {
                    points: centerLinePoints_wgs84,
                    rawRoute: centerLineRoute,
                };
            }
            renderable._renderData.routes.push(routeData);
        }
        return renderable;
    }
    /**
     * Gets the main primitive collection containing all rendered SIMA sub-collections.
     */
    get primitives() {
        return this._primitives;
    }
    /**
     * Gets the polyline collection used for route lines.
     */
    get polylines() {
        return this._polylines;
    }
    /**
     * Gets the label collection used for survey point labels.
     */
    get labels() {
        return this._labels;
    }
    /**
     * Gets the visibility of the rendered primitives and entities.
     */
    get show() {
        return this._primitives ? this._primitives.show : false;
    }
    /**
     * Sets the visibility of the rendered primitives and sphere entities.
     */
    set show(visible) {
        if (this._primitives && !this._primitives.isDestroyed()) {
            this._primitives.show = visible;
        }
        for (const sphere of this._renderData.surveyPointSpheres.values()) {
            sphere.show = visible;
        }
    }
    /**
     * Sets the visibility of the primitives and entities.
     */
    setVisibility(visible) {
        this.show = visible;
    }
    zoomTo() {
        const boundingSphere = this.getBoundingSphere();
        if (this._viewer?.camera && boundingSphere) {
            this._viewer.camera.flyToBoundingSphere(boundingSphere, {
                offset: new HeadingPitchRange(CMath.toRadians(0.0), CMath.toRadians(-30.0), 0.0),
            });
        }
    }
    /**
     * Checks if the SIMA document contains a central line.
     */
    hasCentralLine() {
        return (this._renderData.routes.some((r) => r.centerLine_wgs84 && r.centerLine_wgs84.points.length > 0) || this._sima.hasCentralLine());
    }
    /**
     * Checks if the renderable primitives have been destroyed.
     */
    isDestroyed() {
        return this._primitives ? this._primitives.isDestroyed() : false;
    }
    /**
     * Renders the SIMA document content into Cesium primitives (polylines, labels) and sphere entities.
     */
    render() {
        const primitiveOptions = this._options
            ? {
                show: this._options.show,
            }
            : undefined;
        if (!this._primitives || this._primitives.isDestroyed()) {
            this._primitives = new PrimitiveCollection(primitiveOptions);
        }
        else {
            this.clear();
        }
        const subCollectionOptions = this._options
            ? { show: this._options.show }
            : undefined;
        if (!this._polylines || this._polylines.isDestroyed()) {
            this._polylines = new PolylineCollection(subCollectionOptions);
            this._primitives.add(this._polylines);
        }
        if (!this._labels || this._labels.isDestroyed()) {
            this._labels = new LabelCollection(subCollectionOptions);
            this._primitives.add(this._labels);
        }
        this._renderSurveyPoints();
        this._renderRoutes();
        this.zoomTo();
        return this._primitives;
    }
    /**
     * Clears all rendered polylines, labels, and sphere entities.
     */
    clear() {
        if (this._polylines && !this._polylines.isDestroyed()) {
            this._polylines.removeAll();
        }
        if (this._labels && !this._labels.isDestroyed()) {
            this._labels.removeAll();
        }
        if (this._viewer?.entities) {
            for (const sphere of this._renderData.surveyPointSpheres.values()) {
                this._viewer.entities.remove(sphere);
            }
        }
        this._renderData.surveyPointSpheres.clear();
    }
    /**
     * Cleans up and destroys all Cesium primitives and sphere entities.
     */
    destroy() {
        this.clear();
        if (this._primitives && !this._primitives.isDestroyed()) {
            this._primitives.destroy();
        }
        this._primitives = undefined;
        this._polylines = undefined;
        this._labels = undefined;
    }
    /**
     * Alias for destroy.
     */
    dispose() {
        this.destroy();
    }
    /**
     * Computes a bounding sphere encompassing all survey points in the SIMA document.
     */
    getBoundingSphere() {
        const positions = Array.from(this._renderData.surveyPoints_wgs84.values()).map((p) => p.position);
        if (positions.length === 0) {
            return undefined;
        }
        return BoundingSphere.fromPoints(positions);
    }
    _getKeyOfSurveyPoint(point) {
        return `${point.point_number}-${point.point_name}`;
    }
    _renderSurveyPoints() {
        if (this._renderData.surveyPoints_wgs84.size === 0)
            return;
        const defaultPointColor = this._options?.pointColor ?? Color.CYAN;
        const defaultLabelColor = this._options?.labelColor ?? Color.WHITE;
        for (const [key, point] of this._renderData.surveyPoints_wgs84) {
            const position = point.position;
            if (!position)
                continue;
            if (this._labels && !this._labels.isDestroyed()) {
                const text = point.point_name ||
                    (point.point_number !== undefined ? String(point.point_number) : "");
                this._labels.add({
                    position,
                    text,
                    font: this._options?.font ?? "14px sans-serif",
                    fillColor: defaultLabelColor,
                    outlineColor: Color.BLACK,
                    outlineWidth: 2,
                    style: LabelStyle.FILL_AND_OUTLINE,
                    verticalOrigin: VerticalOrigin.BOTTOM,
                    pixelOffset: new Cartesian2(0, -8),
                });
            }
            if (this._viewer?.entities) {
                const sphereEntity = EarthBrainUtils.addSphere(this._viewer, position, defaultPointColor, 0, this._options?.pointScale ? this._options.pointScale * 3.5 : 3.5);
                if (sphereEntity) {
                    sphereEntity.show = this.show;
                    this._renderData.surveyPointSpheres.set(key, sphereEntity);
                    if (point.point_name) {
                        this._renderData.surveyPointSpheres.set(point.point_name, sphereEntity);
                    }
                    if (point.point_number !== undefined) {
                        this._renderData.surveyPointSpheres.set(String(point.point_number), sphereEntity);
                    }
                }
            }
        }
    }
    _renderRoutes() {
        if (!this._polylines || this._polylines.isDestroyed()) {
            return;
        }
        for (let i = 0; i < this._renderData.routes.length; i++) {
            const route = this._renderData.routes[i];
            const ipPositions = route.ipLine_wgs84.points.map((pt) => pt.position);
            if (ipPositions.length > 1) {
                const routeColor = this._getRouteColor(route.ipLine_wgs84.rawRoute.route_type);
                const material = Material?.fromType
                    ? Material.fromType("Color", { color: routeColor })
                    : undefined;
                this._polylines.add({
                    positions: ipPositions,
                    width: this._options?.lineWidth ?? 2,
                    ...(material ? { material } : {}),
                });
            }
            const mainLinePositions = route.mainLine_wgs84.points.map((pt) => pt.position);
            if (mainLinePositions.length > 1) {
                const mainLineColor = this._getRouteColor(route.mainLine_wgs84.rawRoute.route_type);
                const mainLineMaterial = Material?.fromType
                    ? Material.fromType("Color", { color: mainLineColor })
                    : undefined;
                this._polylines.add({
                    positions: mainLinePositions,
                    width: this._options?.lineWidth ?? 2,
                    ...(mainLineMaterial ? { material: mainLineMaterial } : {}),
                });
            }
            // Rendering of centerline is disabled for now
            // const centerLinePositions = route.centerLine_wgs84.points.map(
            // 	(pt) => pt.position,
            // );
            // if (centerLinePositions.length > 1) {
            // 	const centerLineColor = this._getRouteColor(
            // 		route.centerLine_wgs84.rawRoute.route_type,
            // 	);
            // 	const centerLineMaterial = Material?.fromType
            // 		? Material.fromType("Color", { color: centerLineColor })
            // 		: undefined;
            // 	this._polylines.add({
            // 		positions: centerLinePositions,
            // 		width: this._options?.lineWidth ?? 2,
            // 		...(centerLineMaterial ? { material: centerLineMaterial } : {}),
            // 	});
            // }
        }
    }
    _getRouteColor(routeType) {
        if (this._options?.routeColors && this._options.routeColors[routeType]) {
            return this._options.routeColors[routeType];
        }
        switch (routeType) {
            case SIMARouteType.IP_LINE:
            case 1:
                return Color.YELLOW;
            case SIMARouteType.MAIN_LINE:
            case 2:
                return Color.CYAN;
            case SIMARouteType.CENTRAL_LINE:
            case 3:
                return Color.MAGENTA;
            default:
                return Color.YELLOW;
        }
    }
}
//# sourceMappingURL=cesium-renderable-SIMA.js.map